import { useState } from "react"
import { Link } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  Plus,
  CheckSquare,
  Loader2,
  Trash2,
  Calendar,
  User as UserIcon,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { NewTaskDialog } from "@/components/tasks/NewTaskDialog"
import {
  tasksApi,
  type TaskListItem,
  type TaskView,
  type Priority,
} from "@/lib/api"
import { formatDateTime } from "@shared/utils/date"
import { cn } from "@/lib/utils"

const VIEWS: { value: TaskView; label: string }[] = [
  { value: "mine", label: "My Tasks" },
  { value: "today", label: "Today" },
  { value: "overdue", label: "Overdue" },
  { value: "upcoming", label: "Upcoming 7 Days" },
  { value: "completed", label: "Completed" },
  { value: "all", label: "All Tasks" },
]

const PRIORITY_VARIANT: Record<Priority, "muted" | "default" | "destructive"> = {
  low: "muted",
  medium: "default",
  high: "destructive",
}

export default function TasksPage() {
  const [view, setView] = useState<TaskView>("mine")
  const [newTaskOpen, setNewTaskOpen] = useState(false)

  const list = useQuery({
    queryKey: ["tasks", view],
    queryFn: () => tasksApi.list({ view }),
  })
  const counts = useQuery({
    queryKey: ["tasks-counts"],
    queryFn: () => tasksApi.counts(),
  })

  const actions = (
    <Button onClick={() => setNewTaskOpen(true)}>
      <Plus className="h-4 w-4" />
      New Task
    </Button>
  )

  return (
    <AppShell title="Tasks & Follow-ups" actions={actions}>
      <div className="space-y-4">
        {/* Filter chips */}
        <div className="flex flex-wrap gap-2">
          {VIEWS.map((v) => {
            const badgeCount =
              v.value === "overdue"
                ? counts.data?.overdue
                : v.value === "today"
                  ? counts.data?.today
                  : v.value === "upcoming"
                    ? counts.data?.upcoming
                    : undefined
            return (
              <button
                key={v.value}
                onClick={() => setView(v.value)}
                className={cn(
                  "flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                  view === v.value
                    ? "border-rc-blue-600 bg-rc-blue-600 text-white"
                    : "border-border bg-background hover:bg-accent"
                )}
              >
                {v.label}
                {badgeCount !== undefined && badgeCount > 0 && (
                  <Badge
                    variant={
                      view === v.value ? "outline" : v.value === "overdue" ? "destructive" : "muted"
                    }
                    className={view === v.value ? "border-white/40 text-white" : ""}
                  >
                    {badgeCount}
                  </Badge>
                )}
              </button>
            )
          })}
        </div>

        {/* Tasks list */}
        {list.isLoading ? (
          <Card className="grid place-items-center p-16">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </Card>
        ) : !list.data?.items.length ? (
          <Card className="p-16 text-center">
            <div className="bg-rc-blue-50 text-rc-blue-600 mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full">
              <CheckSquare className="h-7 w-7" />
            </div>
            <h3 className="text-foreground text-lg font-semibold">
              {view === "mine" ? "No open tasks" : "No tasks match this filter"}
            </h3>
            <p className="text-muted-foreground mt-1 text-sm">
              {view === "mine"
                ? "Nice work! Nothing on your plate right now."
                : "Try a different view."}
            </p>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <ul className="divide-border divide-y">
              {list.data.items.map((task) => (
                <TaskRow key={task.id} task={task} />
              ))}
            </ul>
          </Card>
        )}
      </div>

      <NewTaskDialog open={newTaskOpen} onOpenChange={setNewTaskOpen} />
    </AppShell>
  )
}

function TaskRow({ task }: { task: TaskListItem }) {
  const qc = useQueryClient()
  const toggle = useMutation({
    mutationFn: (completed: boolean) => tasksApi.toggle(task.id, completed),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
      qc.invalidateQueries({ queryKey: ["tasks-counts"] })
    },
  })
  const remove = useMutation({
    mutationFn: () => tasksApi.remove(task.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
      qc.invalidateQueries({ queryKey: ["tasks-counts"] })
    },
  })

  const completed = task.status === "completed"
  const overdue = !completed && task.dueAt && task.dueAt < Math.floor(Date.now() / 1000)
  const contactName =
    task.contactBusinessName ??
    (task.contactFirstName ? `${task.contactFirstName} ${task.contactLastName ?? ""}`.trim() : null)

  return (
    <li className="hover:bg-rc-grey-50 flex items-start gap-3 p-4">
      <input
        type="checkbox"
        checked={completed}
        onChange={(e) => toggle.mutate(e.target.checked)}
        className="border-input mt-0.5 h-4 w-4 rounded"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p
            className={cn(
              "text-sm font-medium",
              completed ? "text-muted-foreground line-through" : "text-foreground"
            )}
          >
            {task.title}
          </p>
          {task.priority === "high" && (
            <Badge variant="destructive" className="text-[10px]">
              High
            </Badge>
          )}
          {task.source === "automation" && (
            <Badge variant="info" className="text-[10px]">
              Automation
            </Badge>
          )}
          {overdue && (
            <Badge variant="destructive" className="text-[10px]">
              Overdue
            </Badge>
          )}
        </div>
        {task.description && (
          <p className="text-muted-foreground mt-1 text-xs">{task.description}</p>
        )}
        <div className="text-muted-foreground mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
          {task.dueAt && (
            <span className={cn("flex items-center gap-1", overdue && "text-destructive")}>
              <Calendar className="h-3 w-3" />
              {formatDateTime(task.dueAt)}
            </span>
          )}
          {task.ownerName && (
            <span className="flex items-center gap-1">
              <UserIcon className="h-3 w-3" />
              {task.ownerName}
            </span>
          )}
          {contactName && task.contactId && (
            <Link to={`/contacts/${task.contactId}`} className="hover:text-foreground">
              → {contactName}
            </Link>
          )}
          {task.dealName && task.dealId && (
            <Link to={`/deals/${task.dealId}`} className="hover:text-foreground">
              → {task.dealName}
            </Link>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={PRIORITY_VARIANT[task.priority]} className="text-[10px]">
          {task.priority}
        </Badge>
        <button
          onClick={() => {
            if (confirm("Delete this task?")) remove.mutate()
          }}
          className="text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  )
}
